import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import Terminal from "@/components/Terminal";
import { Skull, Play, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

const STATUS_COL = {
  active: "text-rose-300 border-rose-500/50 bg-rose-500/10",
  completed: "text-emerald-300 border-emerald-500/50 bg-emerald-500/10",
  queued: "text-slate-300 border-slate-700 bg-slate-900/50",
  launching: "text-amber-300 border-amber-500/50 bg-amber-500/10",
};

const AutonomousRedTeam = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [trace, setTrace] = useState([]);
  const [draft, setDraft] = useState("Operation Phoenix");

  const load = () => api.get("/red-team/campaigns").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const launch = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    setTrace([{ type: "cmd", text: `red-team launch --name "${draft}"` }]);
    try {
      const { data: r } = await api.post("/red-team/launch", { name: draft });
      for (let i = 0; i < r.trace.length; i++) {
        await new Promise((res) => setTimeout(res, 350));
        setTrace((prev) => [...prev, { type: "out", text: r.trace[i] }]);
      }
      toast.success(`${r.name} acquired`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Launch failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="p-6 text-cyan-400"><Loader2 className="animate-spin h-5 w-5" /></div>;

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Attack Surface / Red Team"
        title="Autonomous Red Team"
        accent="pink"
        subtitle="AI-driven adversary emulation — continuously probes your environment with adaptive TTPs."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaigns list */}
        <div className="lg:col-span-2 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-1">Active Campaigns</div>
          {data.campaigns.map((c) => (
            <div key={c.id} data-testid={`rt-campaign-${c.id}`} className="csx-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg text-fuchsia-300">{c.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">target: <span className="font-mono text-cyan-300">{c.target}</span> · operator: <span className="text-slate-300">{c.operator}</span></div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_COL[c.status]}`}>
                  {c.status}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.mitre_tactics.map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-slate-900/80 border border-slate-800 text-slate-400">
                    {t}
                  </span>
                ))}
              </div>

              <div className="mt-3">
                <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                  <span>PROGRESS</span><span>{c.progress}%</span>
                </div>
                <div className="h-1.5 bg-slate-800">
                  <div className={`h-full ${c.progress >= 80 ? "bg-emerald-500" : c.progress >= 30 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${c.progress}%` }} />
                </div>
              </div>
            </div>
          ))}

          <div className="csx-card p-4 mt-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 mb-2">Launch New Campaign</div>
            <div className="flex gap-2">
              <input
                data-testid="rt-name"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="flex-1 bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono outline-none focus:border-fuchsia-500/70"
                placeholder="Operation name..."
              />
              <TButton data-testid="rt-launch" icon={busy ? Loader2 : Play} variant="accent" onClick={launch} disabled={busy}>
                {busy ? "Launching" : "Launch"}
              </TButton>
            </div>
            {trace.length > 0 && (
              <div className="mt-3">
                <Terminal lines={trace} height="h-40" prompt="csx@red-team ~$" />
              </div>
            )}
          </div>
        </div>

        {/* MITRE coverage matrix */}
        <div className="csx-card p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3">MITRE ATT&amp;CK Coverage</div>
          <ul className="space-y-2 text-xs">
            {data.mitre_coverage.map((t) => {
              const pct = Math.round((t.covered / t.techniques) * 100);
              const col = pct >= 80 ? "emerald" : pct >= 60 ? "amber" : "rose";
              return (
                <li key={t.id} data-testid={`mitre-${t.id}`} className="border-b border-slate-900 pb-1.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-slate-500">{t.id}</span>
                      <span className="text-slate-200 ml-2">{t.name}</span>
                    </div>
                    <span className={`text-${col}-300 font-mono`}>{t.covered}/{t.techniques}</span>
                  </div>
                  <div className="h-1 bg-slate-800 mt-1">
                    <div className={`h-full bg-${col}-500`} style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AutonomousRedTeam;
